/**
 * Create Role page - Interactive role creation with criteria chatbot
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { roleAPI } from '../api/client';
import ChatMessageComponent from '../components/ChatMessage';
import SuggestionButtons from '../components/SuggestionButtons';
import FinalizationBanner from '../components/FinalizationBanner';
import TopRibbon from '../components/TopRibbon';
import { useCriteriaChatbot } from '../hooks/useCriteriaChatbot';
import './CreateRole.css';

const CreateRole: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleIdParam = searchParams.get('role_id');

  const [step, setStep] = useState<'basic' | 'criteria'>('basic');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [draftRoleId, setDraftRoleId] = useState<string | null>(roleIdParam);

  const [useChatbot, setUseChatbot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const chatbot = useCriteriaChatbot({
    roleTitle: title,
    draftRoleId,
    useChatbot,
  });

  // Use chatbot error for display, but also track local errors
  const [localError, setLocalError] = useState<string | null>(null);
  const error = chatbot.error || localError;
  const setError = (msg: string | null) => {
    setLocalError(msg);
    if (msg === null) chatbot.setError(null);
  };

  // Load draft role if resuming (role_id in URL)
  useEffect(() => {
    if (!draftRoleId) return;

    const loadDraftRole = async (roleId: string) => {
      setLoading(true);
      try {
        const roleResponse = await roleAPI.getRole(roleId);
        const role = roleResponse.data;

        setTitle(role.title);
        setDescription(role.description || '');

        if (role.criteria) {
          chatbot.setCriteriaText(role.criteria);
          chatbot.setIsCriteriaComplete(true);
        }

        try {
          const convResponse = await roleAPI.getConversation(roleId);
          const conversation = convResponse.data;

          if (conversation.conversation_history.length > 0) {
            chatbot.setConversationHistory(conversation.conversation_history);
            setUseChatbot(true);
            setStep('criteria');
          }
        } catch (err) {
          console.log('No conversation found for role', err);
        }
      } catch (err) {
        console.error('Failed to load draft role:', err);
        setError('Failed to load draft role. It may have been deleted.');
      } finally {
        setLoading(false);
      }
    };

    loadDraftRole(draftRoleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRoleId]);

  const handleNext = async () => {
    if (!title.trim()) {
      setError('Please enter a role title');
      return;
    }

    if (draftRoleId) {
      setStep('criteria');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await roleAPI.createRole({
        title: title.trim(),
        description: description?.trim() || undefined,
        status: 'draft'
      });

      const role = response.data;
      setDraftRoleId(role.id);
      window.history.replaceState({}, '', `/create-role?role_id=${role.id}`);
      setStep('criteria');
    } catch (err: any) {
      console.error('Failed to create draft role:', err);
      let errorMessage = 'Failed to create draft role. ';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMessage += err.response.data.detail.map((e: any) =>
            `${e.loc.join('.')}: ${e.msg}`
          ).join(', ');
        } else {
          errorMessage += err.response.data.detail;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!title.trim()) {
      setError('Please enter a role title');
      return;
    }

    if (!chatbot.criteriaText.trim()) {
      setError('Please define screening criteria');
      return;
    }

    if (title.length > 500) {
      setError('Role title is too long (max 500 characters). Please shorten it.');
      return;
    }

    if (description && description.length > 2000) {
      setError('Role description is too long (max 2000 characters). Please shorten it.');
      return;
    }

    if (chatbot.criteriaText.length > 50000) {
      setError('Screening criteria is too long (max 50,000 characters). The chatbot should have kept it shorter - please try regenerating with more concise requirements.');
      return;
    }

    if (chatbot.criteriaText.length < 10) {
      setError('Screening criteria is too short (min 10 characters). Please provide more detail.');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      let roleId: string;

      if (draftRoleId) {
        const response = await roleAPI.updateRole(draftRoleId, {
          title,
          description: description || undefined,
          criteria: chatbot.criteriaText,
          dimensions: chatbot.parsedDimensions || undefined,
          flags: chatbot.parsedFlags || undefined,
          thresholds: chatbot.parsedThresholds || undefined,
          status: 'active'
        });
        roleId = response.data.id;
      } else {
        const response = await roleAPI.createRole({
          title,
          description: description || undefined,
          criteria: chatbot.criteriaText,
          status: 'active'
        });
        roleId = response.data.id;
      }

      navigate(`/roles/${roleId}/upload`);
    } catch (err: any) {
      console.error('Failed to finalize role:', err);
      let errorMessage = 'Failed to finalize role. Please try again.';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map((e: any) =>
            `${e.loc.join('.')}: ${e.msg}`
          ).join(', ');
        } else {
          errorMessage = err.response.data.detail;
        }
      }
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <TopRibbon />
      <div className="create-role-container">
        <div className="create-role-header">
          <button onClick={() => navigate('/roles')} className="back-button">
            &larr; Back to Roles
          </button>
          <h1>Create New Role</h1>
        </div>

      <div className="create-role-content">
        {/* Step 1: Basic Info */}
        <div className={`step-section ${step === 'basic' ? 'active' : ''}`}>
          <div className="step-header">
            <span className="step-number">1</span>
            <h2>Basic Information</h2>
          </div>

          <div className="form-group">
            <label htmlFor="title">
              Role Title <span className="required">*</span>
              <span style={{ float: 'right', fontSize: '0.85em', color: title.length > 500 ? '#dc3545' : '#6c757d', fontWeight: 'normal' }}>
                {title.length} / 500
              </span>
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Senior Backend Engineer"
              className="form-input"
              disabled={step === 'criteria'}
              maxLength={500}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">
              Description (Optional)
              <span style={{ float: 'right', fontSize: '0.85em', color: description.length > 2000 ? '#dc3545' : '#6c757d' }}>
                {description.length} / 2000
              </span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the role..."
              className="form-textarea"
              rows={3}
              disabled={step === 'criteria'}
              maxLength={2000}
            />
            {description.length > 2000 && (
              <div style={{ color: '#dc3545', fontSize: '0.85em', marginTop: '4px' }}>
                Description must be 2000 characters or less
              </div>
            )}
          </div>

          {step === 'basic' && (
            <button
              onClick={handleNext}
              className="btn-next"
              disabled={!title.trim() || loading}
            >
              {loading ? 'Creating draft...' : 'Next: Define Criteria \u2192'}
            </button>
          )}
        </div>

        {/* Step 2: Criteria Definition */}
        {step === 'criteria' && (
          <div className="step-section active">
            <div className="step-header">
              <span className="step-number">2</span>
              <h2>Screening Criteria</h2>
            </div>

            <div className="criteria-mode-selector">
              <button
                onClick={() => setUseChatbot(false)}
                className={!useChatbot ? 'mode-btn active' : 'mode-btn'}
              >
                Manual Entry
              </button>
              <button
                onClick={() => setUseChatbot(true)}
                className={useChatbot ? 'mode-btn active' : 'mode-btn'}
              >
                AI Assistant
              </button>
            </div>

            {!useChatbot ? (
              <div className="manual-criteria">
                <div className="form-group">
                  <label htmlFor="criteria">
                    Screening Criteria <span className="required">*</span>
                  </label>
                  <textarea
                    id="criteria"
                    value={chatbot.criteriaText}
                    onChange={(e) => chatbot.setCriteriaText(e.target.value)}
                    placeholder="Define your screening criteria in natural language..."
                    className="form-textarea criteria-textarea"
                    rows={12}
                  />
                  <div className="criteria-hint">
                    <strong>Example:</strong> "Looking for a Senior Backend Engineer with 5+ years Python,
                    FastAPI, PostgreSQL, AWS experience. Must have team leadership experience.
                    Red flags: frequent job hopping. Green flags: open source contributions."
                  </div>
                </div>
              </div>
            ) : (
              <div className="chatbot-criteria">
                <div className="chat-messages">
                  {chatbot.conversationHistory.map((msg, idx) => (
                    <ChatMessageComponent
                      key={idx}
                      role={msg.role as 'user' | 'assistant'}
                      content={msg.content}
                    />
                  ))}
                  {chatbot.chatLoading && (
                    <ChatMessageComponent
                      role="assistant"
                      content=""
                      isLoading={true}
                    />
                  )}
                </div>

                <FinalizationBanner visible={chatbot.isFinalizationInProgress} />

                {!chatbot.isCriteriaComplete && chatbot.lastSuggestions.length > 0 && (
                  <SuggestionButtons
                    suggestions={chatbot.lastSuggestions}
                    onSelect={(text) => chatbot.setUserMessage(text)}
                    disabled={chatbot.chatLoading}
                  />
                )}

                {chatbot.conversationHistory.length > 0 && !chatbot.isCriteriaComplete && (
                  <div style={{ textAlign: 'center', margin: '12px 0' }}>
                    <button
                      onClick={() => {
                        if (window.confirm('Are you sure you want to start over? This will clear your conversation history.')) {
                          chatbot.resetChatbot();
                        }
                      }}
                      className="btn-start-over"
                      style={{
                        padding: '6px 16px', fontSize: '13px', color: '#6b7280',
                        background: 'transparent', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      }}
                    >
                      Start Over
                    </button>
                  </div>
                )}

                {!chatbot.isCriteriaComplete && (
                  <div className="chat-input-section">
                    <input
                      type="text"
                      value={chatbot.userMessage}
                      onChange={(e) => chatbot.setUserMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !chatbot.chatLoading && chatbot.sendChatMessage()}
                      placeholder="Type your response..."
                      className="chat-input"
                      disabled={chatbot.chatLoading}
                    />
                    <button
                      onClick={chatbot.sendChatMessage}
                      disabled={!chatbot.userMessage.trim() || chatbot.chatLoading}
                      className="btn-send"
                    >
                      Send
                    </button>
                  </div>
                )}

                {chatbot.isCriteriaComplete && (
                  <div className="criteria-complete-banner">
                    Criteria finalized! Review below or create role.
                  </div>
                )}

                {chatbot.criteriaText && (
                  <div className="finalized-criteria">
                    <h4>Finalized Criteria:</h4>
                    <div className="criteria-preview">
                      {chatbot.criteriaText}
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="step-actions">
              <button
                onClick={() => setStep('basic')}
                className="btn-back"
              >
                &larr; Back
              </button>
              <button
                onClick={handleCreateRole}
                disabled={!chatbot.criteriaText.trim() || creating}
                className="btn-create"
              >
                {creating ? 'Creating...' : 'Create Role & Upload Resumes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default CreateRole;
